/* eslint-disable react/no-unstable-nested-components, no-magic-numbers, react/prop-types */
import { AreaChart } from '@mantine/charts';
import type { TSessionByLeads } from '@newo/api/gateway';
import dayjs from '@/libs/dayjs.ts';
// mantine
// components
import ChartTooltip from '@/pages/Report/components/ChartTooltip';
// utils
import { formatDate } from '@/utils/format.ts';
import { useIsDesktop } from '@/pages/Report/Report.hooks.ts';
import {
    CHART_HEIGHT_DESKTOP,
    CHART_HEIGHT_MOBILE
} from '@/pages/Report/Report.utils.ts';
import { cn } from '@/utils';

interface TotalSessionsAreaChartProps {
    data: TSessionByLeads;
}

const SUNDAY = 0;
const SATURDAY = 6;

// Function to check if a date is a weekend (Saturday or Sunday)
const isWeekend = (date: string) => {
    const day = dayjs(date).day();
    return day === SUNDAY || day === SATURDAY;
};

const TotalSessionsAreaChart = ({ data }: TotalSessionsAreaChartProps) => {
    const isDesktop = useIsDesktop();
    const h = isDesktop ? CHART_HEIGHT_DESKTOP : CHART_HEIGHT_MOBILE;
    return (
        <AreaChart
            areaProps={{
                isAnimationActive: true,
                dot: (props) => {
                    const { cx, cy, dataKey, payload, key } = props;
                    const isWeekendDay = payload && isWeekend(payload.date);

                    if (dataKey === 'total_sessions') {
                        return (
                            <circle
                                cx={cx}
                                cy={cy}
                                fill={isWeekendDay ? 'white' : '#9b9b9e'}
                                key={key}
                                r={4}
                                stroke="#9b9b9e"
                                strokeWidth={isWeekendDay ? 2 : 1}
                            />
                        );
                    }

                    return (
                        <circle
                            cx={cx}
                            cy={cy}
                            fill={isWeekendDay ? 'white' : '#2ed1f2'}
                            key={key}
                            r={4}
                            stroke="#2ed1f2"
                            strokeWidth={2}
                        />
                    );
                },
                activeDot: (props) => {
                    const { cx, cy, payload, key } = props;
                    const isWeekendDay = payload && isWeekend(payload.date);
                    return (
                        <circle
                            cx={cx}
                            cy={cy}
                            fill={isWeekendDay ? 'white' : '#f44f15'}
                            key={key}
                            r={4}
                            stroke="#f44f15"
                            strokeWidth={2}
                        />
                    );
                }
            }}
            data={data.items}
            dataKey="date"
            h={h}
            series={[
                { name: 'total_sessions', color: 'gray.4' },
                { name: 'total_sessions_leads', color: 'cyan.6' }
            ]}
            tickLine="xy"
            tooltipProps={{
                wrapperStyle: { fontSize: '10px' },
                content: ({ label, payload }) => {
                    const isWeekendDay = label ? isWeekend(label) : false;
                    const dayName = label ? dayjs(label).format('dddd') : '';
                    return (
                        <ChartTooltip>
                            <div className="font-bold">
                                {label ? formatDate(label, 'MMM DD, YYYY') : ''}{' '}
                                <span
                                    className={cn({
                                        'text-orange-8': isWeekendDay
                                    })}
                                >
                                    ({dayName})
                                </span>
                            </div>
                            <div>{`Total: ${payload.at(0)?.value}`}</div>
                            <div>{`Leads: ${payload.at(1)?.value}`}</div>
                        </ChartTooltip>
                    );
                }
            }}
            type="stacked"
            withYAxis={isDesktop}
            xAxisLabel="Date"
            xAxisProps={{
                tickFormatter: (value) => formatDate(value ?? '', 'MM/DD')
            }}
            yAxisLabel="Sessions"
        />
    );
};

export default TotalSessionsAreaChart;
